import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable, Subject, combineLatest, of } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

import { LaskuToimittaja, LaskuToimittajaIban } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

@Component({
  templateUrl: './toimittaja-selaa.katsele.component.html',
  styleUrls: ['./toimittaja-selaa.katsele.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ToimittajaSelaaKatseleComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()

  readonly asiakas = signal<Asiakas | null>(null)
  toimittajaObservable: Observable<LaskuToimittaja | null>
  ibanitObservable: Observable<LaskuToimittajaIban[]>
  /** Map uid → "etunimi sukunimi" sekä kipien Lemonaid-UID:t, jotta luoja/päivittäjä näkyvät nimellä. */
  kayttajienNimetMapObservable: Observable<Map<string, string>>

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
  ) {
    this.toimittajaObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._route.paramMap]).pipe(
      switchMap(([avainTiedot, params]) => {
        const toimittajaAvain = params.get('toimittajaAvain')
        if (!avainTiedot?.avain || !toimittajaAvain) {
          return of<LaskuToimittaja | null>(null)
        }
        const uri = this._laskuUriService.getLaskuToimittajaUri(avainTiedot.avain, toimittajaAvain)
        return this._firebaseLemonaid
          .firestoreDoc<LaskuToimittaja>(uri)
          .listen()
          .pipe(
            map(doc => {
              if (!doc) {
                return null
              }
              doc.avain = toimittajaAvain
              return doc
            }),
          )
      }),
    )

    this.ibanitObservable = this.toimittajaObservable.pipe(
      map(t => {
        if (!t?.iban) {
          return []
        }
        // Listataan oletus ensin, sitten muut IBANin mukaan
        return Object.values(t.iban).sort((a, b) => {
          if (a.tyyppi === b.tyyppi) {
            return (a.iban ?? '').localeCompare(b.iban ?? '')
          }
          return a.tyyppi === 'oletus' ? -1 : 1
        })
      }),
    )

    this.kayttajienNimetMapObservable = combineLatest([this._asiakasService.nykyisenAsiakkaanKayttajatObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotObservable]).pipe(
      map(([kayttajat, kipit]) => {
        const m = new Map<string, string>()
        for (const k of kayttajat) {
          m.set(k.avain, k.etunimi + ' ' + k.sukunimi)
        }
        for (const kipi of kipit) {
          m.set(kipi.avain, kipi.etunimi + ' ' + kipi.sukunimi)
          if (kipi.lemonaidUid) {
            m.set(kipi.lemonaidUid, kipi.etunimi + ' ' + kipi.sukunimi)
          }
        }
        return m
      }),
    )
  }

  ngOnInit() {
    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas.set(asiakas)
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  /** Palauttaa "Nimi Sukunimi (uid)" tai pelkän uid:n jos nimeä ei löydy. Tyhjälle uid:lle '–'. */
  annaKayttajanNimi(uid: string | null | undefined, nimet: Map<string, string> | null): string {
    if (!uid) {
      return '-'
    }
    const nimi = nimet?.get(uid)
    return nimi ? `${nimi} (${uid})` : uid
  }

  takaisin() {
    if (!this.asiakas()?.avain) {
      return
    }
    this._router.navigate(['asiakkaat', this.asiakas().avain, 'toimittajat', 'selaa'])
  }
}
